import React, { useEffect, useState } from "react";
import { Loader2, Database, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@cms/ui/components/button";
import { Badge } from "@cms/ui/components/badge";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@cms/ui/components/alert-dialog";
import type { ApiClient } from "../lib/api";
import type { AdminClient } from "../lib/outlet-context";

interface SchemaState {
  current: number;
  latest: number;
  pending: number[];
  up_to_date: boolean;
}

/**
 * Core database schema version for one tenant, with a Run migrations action.
 * Reads the authoritative state from the tenant's own D1 (GET .../schema, which
 * also reconciles the global-DB cache); the migrate button brings it current
 * and refreshes the cached schema_version on the client row.
 */
export function SchemaStatus({ client, api, onMigrated }: {
  client: AdminClient;
  api: ApiClient | null;
  onMigrated: (client: AdminClient) => void;
}) {
  const [state, setState] = useState<SchemaState | null>(null);
  const [error, setError] = useState("");
  const [migrating, setMigrating] = useState(false);

  useEffect(() => {
    if (!api || !client.d1_database_id) return;
    let cancelled = false;
    api.request<{ schema: SchemaState }>(`/admin/clients/${client.id}/schema`)
      .then((data) => {
        if (cancelled) return;
        setState(data.schema);
        onMigrated({ ...client, schema_version: data.schema.current });
      })
      .catch((err: Error) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
    // Re-fetch only when the target tenant changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, client.id, client.d1_database_id]);

  const migrate = async () => {
    if (!api) return;
    setMigrating(true);
    setError("");
    try {
      const data = await api.request<{ schema: SchemaState }>(
        `/admin/clients/${client.id}/migrate`, { method: "POST" }
      );
      setState(data.schema);
      onMigrated({ ...client, schema_version: data.schema.current });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setMigrating(false);
    }
  };

  if (!client.d1_database_id) return null;

  const outOfDate = state ? !state.up_to_date : false;

  return (
    <div className="mt-4 border-t pt-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm">
          <Database className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">Schema</span>
          {state ? (
            <span className="font-medium tabular-nums">
              v{state.current}
              {outOfDate && <span className="text-muted-foreground"> · latest v{state.latest}</span>}
            </span>
          ) : (
            <span className="text-muted-foreground">…</span>
          )}
        </div>
        {state && (outOfDate ? (
          <Badge variant="warning" className="gap-1">
            <AlertCircle className="h-3 w-3" />
            Out of date
          </Badge>
        ) : (
          <Badge variant="success" className="gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Up to date
          </Badge>
        ))}
      </div>

      {error && (
        <p className="mt-2 flex items-center gap-1 text-xs text-destructive">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}

      {outOfDate && state && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="outline" className="mt-2 w-full" disabled={migrating}>
              {migrating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Database className="h-3.5 w-3.5" />}
              Run DB migrations
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Migrate {client.business_name || "this client"}'s database?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This applies core schema {state.pending.length === 1 ? "migration" : "migrations"}{" "}
                {state.pending.map((v) => `v${v}`).join(", ")} to the tenant's live database,
                bringing it from v{state.current} to v{state.latest}. Migrations are additive and
                idempotent — no existing data is removed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={migrate}>Run migrations</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
